import React from 'react';
import PropTypes from 'prop-types';
import { Box, Tooltip, Typography, makeStyles } from '@material-ui/core';
import { Info as InfoIcon } from '@material-ui/icons';

import { Cards } from 'src/components';
import { Triangle, TriangleDown } from 'src/utils/mdIcons';
import { formatNumber } from 'src/utils/formatters';


const landscapeParameterDescriptions = [
  {
    title: 'Número de parches',
    description: `Es la cuenta total de parches individuales de un tipo de cobertura específico. Funciona como
      una medida simple de fragmentación; a menudo, un mayor número de parches para una misma área total
      indica una mayor subdivisión del hábitat.`,
  },
  {
    title: 'Área total de parches',
    description: `Es la suma de la superficie de todos los parches de una clase específica, indicando la
      disponibilidad de hábitats asociados a aquella clase.`,
  },
  {
    title: 'Área media de parches',
    description: `Se obtiene dividiendo el área total de una clase entre el número de parches asociados a ella.
      Proporciona una extensión promedio de los fragmentos, lo cual es vital para especies que requieren un
      tamaño mínimo de hábitat para sobrevivir.`,
  },
  {
    title: 'Área núcleo total',
    description: `Es la suma de superficie de hábitat "interior" de todos los parches de una clase. Este hábitat
      está menos afectado por las perturbaciones de la matriz circundante.`,
  },
  {
    title: 'Área núcleo media',
    description: `Corresponde al promedio de área núcleo por parche.`,
  },
  {
    title: 'Índice de área núcleo',
    description: `Es el porcentaje de la superficie de todos los parches que consiste en área núcleo en relación
      con el área total. Un índice bajo indica que la mayor parte del hábitat está expuesta a efectos de borde,
      lo que compromete la integridad estructural.`,
  },
  {
    title: 'Índice de área conectada',
    description: `Mide el grado en que los parches de un mismo tipo están vinculados espacialmente. Evalúa la
      capacidad de movimiento a través del paisaje, siendo un indicador clave de la conectividad y la
      resistencia a la fragmentación.`,
  },
];


const useStyles = makeStyles(theme => ({
  title: {
    fontWeight: 600,
    minWidth: 150,
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  titleHeader: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  subtitle: {
    fontWeight: 500,
    textTransform: 'uppercase',
    color: theme.palette.common.gray600,
    lineHeight: 1,
  },
  group: {
    padding: '3px',
    borderRadius: theme.shape.borderRadius,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.common.gray400,
  },
  trendIconUp: {
    fontSize: '0.5rem',
    fontWeight: 600,
    color: theme.palette.success.main,
  },
  trendIconDown: {
    fontSize: '0.5rem',
    fontWeight: 600,
    color: theme.palette.error.main,
  },
  infoIconHeader: {
    fontSize: '0.95rem',
    color: theme.palette.common.gray600,
    cursor: 'pointer',
    marginTop: 1,
  },
  tooltipWide: {
    maxWidth: 420,
  },
  tooltipParagraph: {
    margin: theme.spacing(0, 0, 1, 0),
    lineHeight: 1.35,
  },
}));

const buildTableData = ({ landscapeData, classTypeId, year, comparisonYear }) => {
  const data = landscapeData.filter(d => d.classTypeId === classTypeId);
  const yearObj = data.find(d => parseInt(d.year, 10) === parseInt(year, 10));
  const yearComparisonObj = data.find(d => parseInt(d.year, 10) === parseInt(comparisonYear, 10));
  if (!yearObj || !yearComparisonObj) {
    return [];
  }

  const calcRelation = obj =>
    obj && obj.totalCoreArea && obj.totalPatchArea
      ? { ...obj, areaCoreRelation: obj.totalCoreArea / obj.totalPatchArea }
      : { ...obj, areaCoreRelation: 0 };

  const yearObjWithRelation = calcRelation(yearObj);
  const yearComparisonObjWithRelation = calcRelation(yearComparisonObj);

  const metrics = [
    { key: 'patchesNumber', name: 'Número de parches', unit: '-' },
    { key: 'totalPatchArea', name: 'Área total de parches', unit: 'ha' },
    { key: 'meanPatchArea', name: 'Área media de parches', unit: 'ha' },
    { key: 'totalCoreArea', name: 'Área núcleo total', unit: 'ha' },
    { key: 'meanCoreArea', name: 'Área núcleo media', unit: 'ha' },
    { key: 'areaCoreIndex', name: 'Índice de área núcleo', unit: '-' },
    { key: 'areaConnectedIndex', name: 'Índice de área conectada', unit: '-' },
  ];

  const getValuation = (isPatchesNumber, variation) => {
    if (variation === 0) {
      return 'Sin variación';
    }
    const isBetter = isPatchesNumber ? variation < 0 : variation > 0;
    return isBetter ? 'Mejora' : 'Empeora';
  };

  return metrics.map(metric => {
    const yearData = yearObjWithRelation[metric.key];
    const comparisonYearData = yearComparisonObjWithRelation[metric.key];

    let variation = 0;
    if (typeof yearData === 'number' && typeof comparisonYearData === 'number' &&
        !isNaN(yearData) && !isNaN(comparisonYearData) && yearData !== 0) {
      variation = ((comparisonYearData - yearData) / yearData) * 100;
    }

    const isPatchesNumber = metric.key === 'patchesNumber';
    return {
      metricKey: metric.key,
      name: metric.name,
      yearData,
      comparisonYearData,
      unit: metric.unit,
      variation,
      valuation: getValuation(isPatchesNumber, variation),
    };
  });
};

const GeoSpatialYearComparison = ({ data, years = [], classTypeId = 'total' }) => {
  const classes = useStyles();

  const tableData = buildTableData({ landscapeData: data, classTypeId, year: years[0], comparisonYear: years[1] });

  const columns = [
    {
      title: (
        <Box className={classes.titleHeader}>
          <span>Parámetro</span>
          <Tooltip
            arrow
            interactive
            placement="top"
            classes={{ tooltip: classes.tooltipWide }}
            title={
              <Box>
                {landscapeParameterDescriptions.map(({ title, description }) => (
                  <Typography key={title} variant="caption" component="p" className={classes.tooltipParagraph}>
                    <strong>{title}:</strong> {description}
                  </Typography>
                ))}
              </Box>
            }
          >
            <InfoIcon className={classes.infoIconHeader} />
          </Tooltip>
        </Box>
      ),
      width: '25%',
      render: itemData => <Typography variant="body2" className={classes.title} color="textPrimary">{itemData.name}</Typography>,
    },
    {
      title: years[0],
      width: '15%',
      render: itemData =>
        <Typography variant="body2" color="textPrimary">{formatNumber(itemData.yearData, 2) || 'Sin dato'}</Typography>,
    },
    {
      title: years[1],
      width: '15%',
      render: itemData =>
        <Typography variant="body2" color="textPrimary">{formatNumber(itemData.comparisonYearData, 2) || 'Sin dato'}</Typography>,
    },
    {
      title: 'Unidad',
      width: '10%',
      render: itemData => <Typography variant="body2" color="textPrimary">{itemData.unit}</Typography>,
    },
    {
      title: 'Variación',
      width: '15%',
      render: itemData =>
        <Box display="flex" alignItems="center">
          {
            itemData.variation > 0 ? <Triangle className={classes.trendIconUp} />
            : itemData.variation < 0 ? <TriangleDown className={classes.trendIconDown} />
            : null
          }
          <Typography variant="body2" color="textPrimary">&nbsp;{`${Math.round(itemData.variation)}%`}</Typography>
        </Box>,
    },
    {
      title: 'Valoración del cambio',
      width: '10%',
      render: itemData => <Typography variant="body2" color="textPrimary">{itemData.valuation}</Typography>,
    },
  ];

  return (
    <Box display="flex" flexDirection="column">
      <Box display="flex" alignItems="center" height={32}>
        <Typography className={classes.subtitle} variant="body2" component="span">
          Parámetros de estructura del paisaje de
          <Typography variant="body2" display="inline" color="primary" component="span">
            <strong> {years[0]} </strong>
          </Typography>
          contra
          <Typography variant="body2" display="inline" color="primary" component="span">
            <strong> {years[1]} </strong>
          </Typography>
        </Typography>
        <Box flexGrow={1} />
        {/* <Box display="flex" className={classes.group} p={0.5} ml={1} >
        </Box> */}
      </Box>
      <Cards columns={columns} values={tableData} variant="condensed" />
    </Box>
  );
};

GeoSpatialYearComparison.propTypes = {
  data: PropTypes.array,
  years: PropTypes.arrayOf(PropTypes.number),
  classTypeId: PropTypes.string,
};


export { GeoSpatialYearComparison };

